import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Headphones, Mic, BookOpen, PenTool, ArrowRight, AlertCircle,
  Search, CheckCircle, Info, Heart, Star
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AuthContext from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScreenerSetup = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);

  // Redirect unauthenticated visitors to /try-demo (wait for auth to finish loading first)
  React.useEffect(() => {
    if (!authLoading && !user) navigate('/try-demo', { replace: true });
  }, [user, authLoading, navigate]);

  // Browser tab title — MOSAIC LPA identity (replaces prior WIDA Screener title)
  React.useEffect(() => {
    const prev = document.title;
    document.title = 'MOSAIC LPA — English Language Proficiency Practice & Assessment';
    return () => { document.title = prev; };
  }, []);

  const [studentName, setStudentName] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [homeLanguage, setHomeLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const isStaff = user && ['teacher', 'school_admin', 'district_admin', 'super_admin'].includes(user.role);

  // Auto-fill name for logged-in users
  React.useEffect(() => {
    if (user) setStudentName(user.name || "");
  }, [user]);

  const grades = [
    { value: "K", label: "Kindergarten" },
    { value: "1", label: "Grade 1" },
    { value: "2", label: "Grade 2" },
    { value: "3-4", label: "Grades 3-4" },
    { value: "5-6", label: "Grades 5-6" },
    { value: "7-8", label: "Grades 7-8" },
    { value: "9-12", label: "Grades 9-12" },
  ];

  const commonLanguages = [
    "Spanish", "Chinese (Mandarin)", "Chinese (Cantonese)", "Arabic", "Bengali",
    "Haitian Creole", "Russian", "Korean", "Urdu", "French",
    "Polish", "Tagalog", "Japanese", "Yoruba", "Igbo", "Other"
  ];

  // MOSAIC LPA domains — four language proficiency areas, no unsupported item counts or durations.
  const lpaDomains = [
    { icon: Headphones, name: "Listening", color: "bg-purple-100 text-purple-700" },
    { icon: Mic, name: "Speaking", color: "bg-blue-100 text-blue-700" },
    { icon: BookOpen, name: "Reading", color: "bg-green-100 text-green-700" },
    { icon: PenTool, name: "Writing", color: "bg-orange-100 text-orange-700" },
  ];

  const handleStartScreener = async () => {
    if (!studentName.trim()) {
      toast.error("Please enter a student name");
      return;
    }
    if (!gradeLevel) {
      toast.error("Please select a grade level");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        student_name: studentName.trim(),
        grade_level: gradeLevel,
        exam_mode: "digital",
        exam_type: "screener",
        home_language: homeLanguage
      };
      if (user) payload.student_id = user.user_id;
      const response = await axios.post(`${API}/exam-sessions`, payload);
      navigate(`/exam/${response.data.id}?type=screener`);
    } catch (error) {
      console.error("Error starting MOSAIC LPA session:", error);
      toast.error("Failed to start. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="screener-setup-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-[#002FA7]">Home</Link>
        <span>/</span>
        <span className="text-gray-900" data-testid="screener-breadcrumb">MOSAIC LPA</span>
      </div>

      {/* Hero Header */}
      <div className="border-2 border-black p-8 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center">
            <Search size={24} />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="screener-heading">
              MOSAIC LPA
            </h1>
            <p className="text-sm text-gray-500">English Language Proficiency Practice &amp; Assessment</p>
          </div>
        </div>
        <p className="text-gray-600 max-w-3xl mb-3">
          MOSAIC LPA provides independent, grade-appropriate English language proficiency practice
          across Listening, Speaking, Reading, and Writing. It gives teachers practical resources to
          observe language skills, review student work, and support instructional planning.
        </p>
        <p className="text-gray-600 max-w-3xl text-sm">
          MOSAIC LPA is an independent educational assessment resource. It does not replace an official
          state-required eligibility or placement assessment.
        </p>
      </div>

      {/* MOSAIC LPA vs MOSAIC Practice Resources cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="border-2 border-[#002FA7] bg-blue-50 p-6" data-testid="card-mosaic-lpa">
          <div className="flex items-center gap-2 mb-3">
            <Search size={20} className="text-[#002FA7]" />
            <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>MOSAIC LPA</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
              <span>English language proficiency practice</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
              <span>Listening, Speaking, Reading, and Writing</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
              <span>Grade-appropriate activities</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
              <span>Teacher-guided assessment resources</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
              <span>Supports instructional decisions</span>
            </li>
          </ul>
        </div>

        <div className="border-2 border-gray-300 bg-gray-50 p-6" data-testid="card-mosaic-practice-resources">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={20} className="text-gray-700" />
            <h3 className="font-bold text-lg text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>MOSAIC Practice Resources</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
              <span>Student assessment activities</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
              <span>Teacher-facing administration resources</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
              <span>Scoring guidance where supported</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
              <span>Practical classroom language development</span>
            </li>
          </ul>
          <Link to="/grades" className="mt-4 inline-block" data-testid="link-mosaic-practice-resources">
            <Button variant="outline" size="sm" className="btn-sharp text-xs">
              Explore MOSAIC Resources
            </Button>
          </Link>
        </div>
      </div>

      {/* MOSAIC LPA Structure */}
      <div className="border-2 border-gray-200 mb-8" data-testid="lpa-structure">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            MOSAIC LPA Structure
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Language proficiency practice across Listening, Speaking, Reading, and Writing, with
            grade-appropriate assessment activities.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-gray-200">
          {lpaDomains.map((domain) => (
            <div key={domain.name} className="swiss-card p-4" data-testid={`lpa-domain-${domain.name.toLowerCase()}`}>
              <div className={`w-10 h-10 ${domain.color} flex items-center justify-center mb-3`}>
                <domain.icon size={20} />
              </div>
              <div className="font-semibold">{domain.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Setup Form */}
      <div className="border-2 border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isStaff ? 'MOSAIC LPA Preview' : 'Student Information'}
          </h2>
          {isStaff && (
            <p className="text-sm text-amber-600 mt-1">You are previewing MOSAIC LPA as a teacher. Results will not count toward student data.</p>
          )}
        </div>
        <div className="p-6 space-y-6">
          <div>
            <Label htmlFor="screenerStudentName" className="text-sm font-semibold mb-2 block">
              {isStaff ? 'Your Name' : 'Student Name *'}
            </Label>
            <Input
              id="screenerStudentName"
              type="text"
              placeholder={isStaff ? 'Your name (auto-filled)' : "Enter student's full name"}
              value={studentName}
              onChange={(e) => { if (!user) setStudentName(e.target.value); }}
              readOnly={!!user}
              className={`h-12 border-2 border-gray-300 focus:border-[#002FA7] rounded-none ${user ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              data-testid="screener-student-name"
            />
            {isStaff && (
              <p className="text-xs text-gray-400 mt-1">Name is set from your account. This is a preview — results are excluded from analytics.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Grade Level *</Label>
              <Select value={gradeLevel} onValueChange={setGradeLevel}>
                <SelectTrigger className="h-12 border-2 rounded-none" data-testid="screener-grade-select">
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  {grades.map(g => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Home Language</Label>
              <Select value={homeLanguage} onValueChange={setHomeLanguage}>
                <SelectTrigger className="h-12 border-2 rounded-none" data-testid="screener-language-select">
                  <SelectValue placeholder="Select home language" />
                </SelectTrigger>
                <SelectContent>
                  {commonLanguages.map(lang => (
                    <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Independent-assessment notice (replaces prior NY State / official-equivalence block) */}
      <div className="bg-blue-50 border-2 border-blue-200 p-6 mb-8" data-testid="lpa-independent-notice">
        <div className="flex gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-blue-800 mb-2">Independent Assessment Resource</h3>
            <p className="text-sm text-blue-700">
              MOSAIC LPA is an independent educational assessment resource. It does not replace an
              official state-required eligibility or placement assessment and does not determine
              English Language Learner eligibility or state placement.
            </p>
          </div>
        </div>
      </div>

      {/* Teacher tips (retained — practical classroom guidance, not an official-equivalence claim) */}
      {gradeLevel && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 p-6 mb-8 rounded-sm" data-testid="lpa-tips">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="text-indigo-600" size={22} />
            <h3 className="font-bold text-indigo-900 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Practice Session Tips
            </h3>
          </div>
          <p className="text-indigo-800 font-medium text-sm mb-3">
            MOSAIC LPA gives teachers a way to observe what a student can do with English right now.
            Reassure students there are no wrong answers.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { icon: Star, text: "Let the student take their time. If they seem anxious, take a short break and try again." },
              { icon: Headphones, text: "For Listening, make sure the audio is clear and at a comfortable volume. Let them look at the picture first." },
              { icon: Mic, text: "For Speaking, accept any response — single words, phrases, or sentences all demonstrate proficiency." },
              { icon: Heart, text: "If the student is very new to English, it's okay to explain in their home language that this helps their teacher plan support." },
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/70 p-3 rounded-sm border border-indigo-100">
                <tip.icon className="text-indigo-500 flex-shrink-0 mt-0.5" size={16} />
                <span className="text-xs text-indigo-800">{tip.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Administration notes — neutral teacher guidance (replaces prior ELL-eligibility and SIFE/ELL tracking claims) */}
      <div className="bg-yellow-50 border-2 border-yellow-300 p-6 mb-8" data-testid="lpa-admin-notes">
        <div className="flex gap-3">
          <AlertCircle className="text-yellow-600 flex-shrink-0" size={24} />
          <div>
            <h3 className="font-bold text-yellow-800 mb-2">Administration Notes</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>- MOSAIC LPA is best administered individually (one-on-one) so the teacher can observe responses.</li>
              <li>- The Speaking section is observed and recorded by the teacher during the session.</li>
              <li>- Results are for instructional planning and classroom use; they are not an official eligibility or placement determination.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={handleStartScreener}
          disabled={loading || !studentName.trim() || !gradeLevel}
          className="btn-sharp-primary h-14 px-8 text-lg flex-1"
          data-testid="start-screener-btn"
        >
          {loading ? (
            <>
              <div className="loading-spinner w-5 h-5 mr-2 border-white border-t-transparent"></div>
              Starting...
            </>
          ) : (
            <>
              Start MOSAIC LPA
              <ArrowRight className="ml-2" size={20} />
            </>
          )}
        </Button>
        <Link to="/">
          <Button variant="outline" className="btn-sharp h-14 px-8 w-full sm:w-auto">
            Back to Home
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default ScreenerSetup;
